"use client";

import { useCallback, useMemo } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { addDays, differenceInCalendarDays, format, parseISO } from "date-fns";
import type { DateRange } from "react-day-picker";

const DEFAULT_CAPACITY = 1;

const toDateString = (date: Date): string => format(date, "yyyy-MM-dd");

const parseDateParam = (value: string | null): Date | null => {
  if (!value) return null;
  const parsed = parseISO(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const parseCapacityParam = (value: string | null): number => {
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed < 1) return DEFAULT_CAPACITY;
  return parsed;
};

/**
 * PDP stay window — kept in the URL so prices, nights, and redirect share one
 * source. Capacity lives here too: the pricing endpoint takes it as a request
 * field, so the desktop sidebar and the mobile tab must agree on it or they
 * would quote different totals for the same stay.
 *
 * Stay-window edits use the History API instead of `router.push`. App Router
 * treats searchParam changes as a server navigation, which would re-fetch the
 * detail page. `pushState` keeps the URL shareable while `useSearchParams`
 * still updates; React Query then refetches prices in the browser so the
 * price card can show its pending state. See Next.js “Using the native
 * History API”.
 */
export function usePdpStayDates() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const { checkIn, checkOut, from, to, nights, capacity, dateRange } =
    useMemo(() => {
      const today = new Date();
      const tomorrow = addDays(today, 1);
      const checkIn = searchParams.get("checkIn") || toDateString(today);
      const checkOut = searchParams.get("checkOut") || toDateString(tomorrow);
      const from = parseDateParam(checkIn) ?? today;
      const to = parseDateParam(checkOut) ?? tomorrow;

      return {
        checkIn,
        checkOut,
        from,
        to,
        nights: Math.max(differenceInCalendarDays(to, from), 1),
        capacity: parseCapacityParam(searchParams.get("capacity")),
        dateRange: { from, to } satisfies DateRange,
      };
    }, [searchParams]);

  const pushParams = useCallback(
    (next: Record<string, string>) => {
      const params = new URLSearchParams(searchParams.toString());
      let changed = false;

      for (const [key, value] of Object.entries(next)) {
        if (params.get(key) === value) continue;
        params.set(key, value);
        changed = true;
      }

      if (!changed) return;

      const query = params.toString();
      const href = query ? `${pathname}?${query}` : pathname;
      window.history.pushState(null, "", href);
    },
    [pathname, searchParams],
  );

  const setStayDates = useCallback(
    (range: DateRange | undefined) => {
      if (!range?.from || !range?.to) return;
      pushParams({
        checkIn: toDateString(range.from),
        checkOut: toDateString(range.to),
      });
    },
    [pushParams],
  );

  const setCapacity = useCallback(
    (next: number) => {
      if (!Number.isInteger(next) || next < 1) return;
      pushParams({ capacity: String(next) });
    },
    [pushParams],
  );

  /** Dates and guests in one navigation — the mobile booking popover applies both at once. */
  const setStay = useCallback(
    (range: DateRange | undefined, nextCapacity?: number) => {
      const next: Record<string, string> = {};
      if (range?.from && range?.to) {
        next.checkIn = toDateString(range.from);
        next.checkOut = toDateString(range.to);
      }
      if (Number.isInteger(nextCapacity) && (nextCapacity as number) >= 1) {
        next.capacity = String(nextCapacity);
      }
      if (Object.keys(next).length === 0) return;
      pushParams(next);
    },
    [pushParams],
  );

  return {
    checkIn,
    checkOut,
    from,
    to,
    nights,
    capacity,
    dateRange,
    setStayDates,
    setCapacity,
    setStay,
  };
}
